<script lang="ts">
    import {
        isJobInDisplayRange,
        getDurationSecInPx,
        getPxFromStart,
    } from "./scheduler";
    import DragJob from "./svelte/DragJob.svelte";
    import type {
        RelatedJobMap,
        SetView,
        View, SchedularVehicles, VehicleAssignmentMap, SchedularVehicleAssignment, SiteVisitMap
    } from './types';
    import {getUserTopOffset, splitOnWeekends} from "./lib";
    import {getJobOffset, getOverlappingJobs, type OverlappingJobs} from "./overlappingJobs.svelte";
    import SchedulerBlocks from "./svelte/schedulerBlocks.svelte";
    import Users from "./svelte/users.svelte";
    import SchedulerColLines from "./svelte/schedulerColLines.svelte";
    import {
        zoomRanges,
        getZoomWithBlocks,
    } from "./zoom2.svelte.js";
    import SchedulerRows from "./svelte/schedulerRows.svelte";
    import type {ValidationStoreInner} from "./validate";
    import NowLine from "./svelte/nowLine.svelte";
    import {SvelteMap, SvelteDate} from "svelte/reactivity";
    import {
        isStateJob,
        isStateNull,
        type PageStateAny,
    } from "./state";
    type Props = {
        setView: SetView;
        view: View;
        totalPxToDisplay: number;
        vehicleAssignmentsStore: VehicleAssignmentMap;
        siteVisitsStore?: SiteVisitMap;
        overlappingJobs: OverlappingJobs;
        vehicles: SchedularVehicles;
        dragTargetDiv: HTMLDivElement;
        contentDiv: HTMLDivElement;
        usersWidth: number;
        rowStepPx: number;
        pageState: PageStateAny;
        validationStore: ValidationStoreInner;
        relatedJobsMap: RelatedJobMap;
        onMouseDownPos: (e: MouseEvent, boundingRect: DOMRect, filteredVehicles: SchedularVehicles, filteredOverlappingJobs: OverlappingJobs) => void;
        onMouseDownRightDur: (
            e: MouseEvent,
            startDragDur: number,
            boundingRect: DOMRect,
            filteredVehicles: SchedularVehicles,
            filteredOverlappingJobs: OverlappingJobs,
        ) => void;
        onMouseDownLeftDur: (e: MouseEvent, boundingRect: DOMRect, filteredVehicles: SchedularVehicles, filteredOverlappingJobs: OverlappingJobs) => void;
        filteredVehiclesOut?: SchedularVehicles;
        filteredOverlappingJobsOut?: OverlappingJobs;
        viewMode?: 'day' | 'week' | 'month';
        onJobDoubleClick?: () => void;
    };
    let {
        view,
        totalPxToDisplay,
        vehicleAssignmentsStore,
        siteVisitsStore,
        overlappingJobs,
        vehicles = $bindable(),
        dragTargetDiv = $bindable(),
        contentDiv = $bindable(),
        usersWidth = $bindable(),
        rowStepPx,
        pageState,
        validationStore,
        relatedJobsMap,
        onMouseDownPos,
        onMouseDownLeftDur,
        onMouseDownRightDur,
        setView,
        filteredVehiclesOut = $bindable(),
        filteredOverlappingJobsOut = $bindable(),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        viewMode = 'week',
        onJobDoubleClick,
    }: Props = $props();


    let rowLineHeight = $state(0);

    const computeLeftDateMs = (): number | null => {
        if (!contentDiv || !view.currentZoomStore.blocks?.[0]) return null;
        const firstDateMs = view.currentZoomStore.blocks[0].start.getTime();
        const offsetMs =
            contentDiv.scrollLeft *
            (view.currentZoomStore.zoomRangePreset.cellMs /
                view.currentZoomStore.cellPx);
        return firstDateMs + offsetMs;
    };

    const onScrollStop = () => {
        const leftDateMs = computeLeftDateMs();
        if (leftDateMs === null) return;
        const leftDate = new SvelteDate(leftDateMs);
        setView({
            currentZoomStore:
                getZoomWithBlocks(
                    view.currentZoomStore.scrollLevel,
                    zoomRanges,
                    leftDate,
                    totalPxToDisplay,
                ) ?? view.currentZoomStore,
            displayDate: leftDate,
        });
    };

    // Debounced scroll-stop detection that fires for ANY scroll source: native
    // touch scroll, momentum scroll after finger-lift, mouse-drag pan, and
    // wheel zoom. Without this, native touch scrolling on mobile would never
    // trigger a re-fetch of jobs in the new visible range.
    const SCROLL_END_DEBOUNCE_MS = 150;
    // setView() programmatically updates scrollLeft to align with the new
    // block boundary, which itself fires a scroll event. Dedupe by tracking
    // the date at the left edge: if it hasn't moved by more than this delta
    // since the last handled stop, treat the scroll as a no-op.
    const SCROLL_LEFT_DATE_DEDUPE_MS = 60 * 1000; // 60 seconds
    let scrollEndTimer: ReturnType<typeof setTimeout> | null = null;
    let lastHandledLeftDateMs: number | null = null;

    const triggerScrollStop = () => {
        const leftDateMs = computeLeftDateMs();
        if (leftDateMs === null) return;
        if (lastHandledLeftDateMs !== null &&
            Math.abs(leftDateMs - lastHandledLeftDateMs) < SCROLL_LEFT_DATE_DEDUPE_MS) {
            return;
        }
        lastHandledLeftDateMs = leftDateMs;
        onScrollStop();
    };

    const onWrapperScroll = () => {
        if (scrollEndTimer !== null) clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => {
            scrollEndTimer = null;
            triggerScrollStop();
        }, SCROLL_END_DEBOUNCE_MS);
    };

    // Minimum pixels the scroll must move before triggering a re-fetch on mouseup
    const SCROLL_DRAG_THRESHOLD_PX = 300;

    let dragStart: {
        e: MouseEvent;
        initialScrollLeft: number;
        initialScrollTop: number;
    } | null = null;
    let scrollDragExceededThreshold = false;

    const onMouseDownScroll = (e: MouseEvent) => {
        if (e.button !== 0) return;
        scrollDragExceededThreshold = false;
        dragStart = {
            e,
            initialScrollLeft: contentDiv.scrollLeft,
            initialScrollTop: contentDiv.scrollTop,
        };
    };

    const onMouseMoveScroll = (e: MouseEvent) => {
        if (dragStart === null || pageState.selected?.dragging) return;
        e.preventDefault();
        document.getSelection()?.empty();

        // check if the mouse is currently within the scheduler box
        // TODO: this currently doesnt work since the drag counts as a selection so when it leaves the box it selects the content and scrolls. Find a way to stop this
        const wrapperRect = contentDiv.getBoundingClientRect();
        if (
            e.pageX > wrapperRect.right ||
            e.pageX < wrapperRect.left ||
            e.pageY > wrapperRect.bottom ||
            e.pageY < wrapperRect.top
        )
            return;

        const dx = dragStart.e.clientX - e.clientX;
        const dy = dragStart.e.clientY - e.clientY;
        if (!scrollDragExceededThreshold && Math.sqrt(dx * dx + dy * dy) >= SCROLL_DRAG_THRESHOLD_PX) {
            scrollDragExceededThreshold = true;
        }

        contentDiv.scrollLeft =
            dragStart.initialScrollLeft + dx;
        contentDiv.scrollTop =
            dragStart.initialScrollTop + dy;
    };

    const onMouseUpScroll = () => {
        dragStart = null;
    };

    const onWheelZoom = (e: MouseEvent) => {
        if (e.ctrlKey == false) return;
        const deltaY = e.wheelDeltaY;
        if (deltaY === 0) return;
        e.preventDefault();
        const boundingRect = contentDiv.getBoundingClientRect();
        const xScrollPx =
            e.x - boundingRect.x + contentDiv.scrollLeft - usersWidth;
        const xScrollMs =
            xScrollPx *
            (view.currentZoomStore.zoomRangePreset.cellMs /
                view.currentZoomStore.cellPx);
        const mouseDateMs =
            view.currentZoomStore.blocks[0].start.getTime() + xScrollMs;
        const mouseDate = new SvelteDate(mouseDateMs);
        const newScrollLevel = Math.max(
            Math.min(
                view.currentZoomStore.scrollLevel - deltaY,
                zoomRanges[0].scrollLevelMax,
            ),
            0,
        );
        const xOffsetPx =
            e.x - Math.ceil(boundingRect.x) - Math.ceil(usersWidth);
        setView({
            currentZoomStore:
                getZoomWithBlocks(
                    newScrollLevel,
                    zoomRanges,
                    mouseDate,
                    totalPxToDisplay,
                ) ?? view.currentZoomStore,
            displayDate: mouseDate,
            scrollLeft: xOffsetPx,
        });
    };

    // TODO: this needs to be refactored so that it doesnt interfere with actual keyboard use in input fields and scroll bar
    const onKeyDown = (e: KeyboardEvent) => {
        if (pageState.selected == null) {
            return;
        }
        // TODO: check if the user is inputing a field before, it probably shouldn't be checking if anything is selected
        switch (e.key) {
            case "Escape":
                e.preventDefault();
                pageState.selected = null;
                pageState.searchStr = '';
                break;
            case "ArrowUp":
                e.preventDefault();
                onUpKeyPress();
                break;
            case "ArrowDown":
                e.preventDefault();
                onDownKeyPress();
                break;
            case "ArrowLeft":
                e.preventDefault();
                onLeftKeyPress();
                break;
            case "ArrowRight":
                e.preventDefault();
                onRightKeyPress();
                break;
        }
    };

    const onLeftKeyPress = () => {
        if (isStateJob(pageState)) {
            if (pageState.selected.item.start === null) return;
            pageState.selected?.item.start.setMinutes(
                pageState.selected?.item.start.getMinutes() - 1,
            );
            return;
        }
    };

    const onRightKeyPress = () => {
        if (isStateJob(pageState)) {
            if (pageState.selected.item.start === null) return;
            pageState.selected?.item.start.setMinutes(
                pageState.selected?.item.start.getMinutes() + 1,
            );
            return;
        }
    };

    const onDownKeyPress = () => {
        if (isStateNull(pageState)) return;
        const job = isStateJob(pageState)
            ? pageState.selected.item
            : vehicleAssignmentsStore.get(
                pageState.selected.item.vehicle.values().next().value!,
            )!;

        if (job.vehicle == null) return;
        const vehiclesIndex = filteredVehicles.findIndex(
            (vehicle) => vehicle.id == job.vehicle?.id,
        )!;
        const newVehicleIndex = Math.min(filteredVehicles.length - 1, vehiclesIndex + 1);
        if (isStateJob(pageState)) {
            pageState.selected.item.vehicle = filteredVehicles[newVehicleIndex];
            return;
        }
        return;
    };

    const onUpKeyPress = () => {
        if (isStateNull(pageState)) return;
        const job = isStateJob(pageState)
            ? pageState.selected.item
            : vehicleAssignmentsStore.get(
                pageState.selected.item.vehicle.values().next().value!,
            )!;

        if (job.vehicle == null) return;
        const vehicleIndex = filteredVehicles.findIndex(
            (vehicle) => vehicle.id == job.vehicle?.id,
        )!;
        const newVehicleIndex = Math.max(0, vehicleIndex - 1);
        if (isStateJob(pageState)) {
            pageState.selected.item.vehicle = filteredVehicles[newVehicleIndex];
            return;
        }
        return;
    };

    let getJobLeftOffset = $derived((job: SchedularVehicleAssignment) => {
        if (job.start === null) {
            return 0;
        }
        return getPxFromStart(
            view.currentZoomStore,
            job.start,
        );
    });

    let vehicleSearch = $state("");
    let filteredVehicles: SchedularVehicles = $derived.by(() => {
        const term = vehicleSearch.trim().toLowerCase();

        // nothing typed → return all
        if (!term) return vehicles;

        const matches = vehicles.filter((vehicle) =>
            (vehicle.registration ?? "").toLowerCase().includes(term)
        );

        return matches.length > 0 ? matches : vehicles;
    });

    let filteredJobs = $derived.by(() => {
        const matches = filteredVehicles;
        const matchedVehicleIds = new Set(matches.map(v => v.id));

        // return only jobs whose vehicle is in matches, OR if the job is currently selected
        const jobs = Array.from(vehicleAssignmentsStore.values()).filter(job => {
            // Include jobs with no vehicle (unscheduled jobs)
            if (!job.vehicle) return true;

            // Always include the selected/dragging job even if its vehicle is filtered out
            const isSelected = isStateJob(pageState) && pageState.selected?.item?.id === job.id;
            if (isSelected) {
                return true;
            }

            // Check if job's vehicle ID is in the filtered vehicles set
            const isInFilteredVehicle = matchedVehicleIds.has(job.vehicle.id);
            return isInFilteredVehicle;
        });

        return jobs;
    });

    // Recalculate overlapping jobs for filtered vehicles only
    let filteredOverlappingJobs = $derived.by(() => {
        // Create a new map with only the filtered vehicles
        const filteredJobsMap = new SvelteMap<string, SchedularVehicleAssignment>();
        for (const job of filteredJobs) {
            filteredJobsMap.set(job.id, job);
        }
        // Include site visits in the overlap calculation
        if (siteVisitsStore) {
            const matchedVehicleIds = new Set(filteredVehicles.map(v => v.id));
            for (const siteVisit of siteVisitsStore.values()) {
                // Only include site visits that have a vehicle assigned and are in filtered vehicles
                if (siteVisit.vehicle && matchedVehicleIds.has(siteVisit.vehicle.id)) {
                    // Cast site visit to job-like structure for overlap calculation
                    filteredJobsMap.set(siteVisit.id, siteVisit as unknown as SchedularVehicleAssignment);
                }
            }
        }
        // Recalculate overlapping jobs
        return getOverlappingJobs(filteredJobsMap);
    });

    // Export filtered values to parent
    $effect(() => {
        filteredVehiclesOut = filteredVehicles;
        filteredOverlappingJobsOut = filteredOverlappingJobs;
    });

    let getJobTopOffset = $derived((job: SchedularVehicleAssignment) => {
        const jobVehicle = job.vehicle;

        if (jobVehicle === null) {
            return 0;
        }

        // When filtering, use the full vehicles list and overlappingJobs if the job's vehicle is filtered out
        const vehicleInFiltered = filteredVehicles.find((vehicle) => vehicle.id === jobVehicle.id);

        // If vehicle is not in filtered list, use the original data (for drop target preview)
        if (!vehicleInFiltered) {
            const userTopOffset = getUserTopOffset(
                vehicles,
                jobVehicle.id,
                overlappingJobs,
            ) || 0;
            const jobOffset = getJobOffset(
                job,
                jobVehicle,
                overlappingJobs,
            ) || 0;
            return (userTopOffset + jobOffset) * rowStepPx;
        }

        // Vehicle is in filtered list, use filtered data
        let userTopOffset = getUserTopOffset(
            filteredVehicles,
            jobVehicle.id,
            filteredOverlappingJobs,
        ) || 0;
        let jobOffset = getJobOffset(
            job,
            vehicleInFiltered,
            filteredOverlappingJobs,
        ) || 0;
        return (userTopOffset + jobOffset) * rowStepPx;
    });

    // Helper to get top offset for a vehicle (used by drop preview)
    const getJobTopOffsetForVehicle = (
        vehicle: { id: string },
        vehiclesList: SchedularVehicles,
        overlappingJobsMap: OverlappingJobs
    ) => {
        const userTopOffset = getUserTopOffset(
            vehiclesList,
            vehicle.id,
            overlappingJobsMap,
        ) || 0;
        return userTopOffset * rowStepPx;
    };
</script>
<svelte:document
    onmousemove={onMouseMoveScroll}
    onmouseup={onMouseUpScroll}
    onkeydown={onKeyDown}
/>

<div
    role="none"
    class="wrapper"
    bind:this={contentDiv}
    onmouseup={() => { if (scrollDragExceededThreshold) triggerScrollStop(); }}
    onmousedown={onMouseDownScroll}
    onscroll={onWrapperScroll}
    style:--users-width={`${usersWidth}px`}
>
    <div class="users-header z-50 bg-background p-2">
        <input
            type="text"
            placeholder="Filter Vehicles..."
            bind:value={vehicleSearch}
            class="w-full rounded border h-10 px-2 focus:outline-none"
        />
    </div>
    <div class="users">
        <Users vehicles={filteredVehicles ?? vehicles} {rowStepPx} overlappingJobs={filteredOverlappingJobs}/>
    </div>
    <div class="scheduler-header">
        <SchedulerBlocks currentZoom={view.currentZoomStore} {rowLineHeight}/>
    </div>
    <div class="now-line-wrapper">
        <NowLine currentZoom={view.currentZoomStore}/>
    </div>
    <div class="content" onwheel={onWheelZoom}>
        <div
            class="rows-wrapper"
            bind:this={dragTargetDiv}
            bind:clientHeight={rowLineHeight}
        >
            <SchedulerRows
                sites={filteredVehicles ?? vehicles}
                overlappingJobs={filteredOverlappingJobs}
                {pageState}
                {rowStepPx}
                zoom={view.currentZoomStore}
            />
            <div class="jobs">
                {#each filteredJobs.values() as job(job.id)}
                    {#if job.start !== null && job.vehicle !== null && job.duration !== null && isJobInDisplayRange({
                        zoom: view.currentZoomStore,
                        jobStart: job.start,
                        jobDurationSec: job.duration
                    },)}
                        {@const isDragging =
                            pageState.selected?.item?.id === job.id &&
                            !!pageState.selected.dragging &&
                            (
                                pageState.selected.dragging.type !== 'pos' ||
                                pageState.selected.dragging.dragStarted === true
                            )}
                        {@const segments = isDragging
                            ? [{ start: job.start, durationSec: job.duration }]
                            : splitOnWeekends(job.start, job.duration, job.weekend_overrides)}
                        {@const jobLeftPx = getJobLeftOffset(job)}
                        {@const jobWidthPx = getDurationSecInPx(view.currentZoomStore, job.duration)}
                        {#each segments as segment, segIdx (segIdx)}
                            {@const isFirst = segIdx === 0}
                            {@const isLast = segIdx === segments.length - 1}
                            {@const segmentLeftPx = getPxFromStart(view.currentZoomStore, segment.start)}
                            {@const pos = isDragging
                                ? pageState.selected.dragging.hoveringPos
                                : {
                                    left: 0,
                                    top: 0,
                                    width: getDurationSecInPx(
                                        view.currentZoomStore,
                                        segment.durationSec,
                                    ),
                                }}
                            <div
                                class="job-wrapper"
                                data-start={segment.start.toLocaleString()}
                                data-cell-step={view.currentZoomStore.cellPx}
                                style:left={`${isDragging ? jobLeftPx : segmentLeftPx}px`}
                                style:top={`${getJobTopOffset(job)}px`}
                                style="position: absolute;"
                            >
                                <DragJob
                                    {job}
                                    onDragPosStart={(e, rect) =>
                                        onMouseDownPos(e, rect, filteredVehicles, filteredOverlappingJobs)}
                                    onDragDurRightStart={(e, startDragDur, rect) =>
                                        onMouseDownRightDur(e, startDragDur, rect, filteredVehicles, filteredOverlappingJobs)}
                                    onDragDurLeftStart={(e, leftRect) =>
                                        onMouseDownLeftDur(e, leftRect, filteredVehicles, filteredOverlappingJobs)}
                                    left={pos.left}
                                    top={pos.top}
                                    widthPx={pos.width}
                                    heightPx={rowStepPx}
                                    isDraggingPos={pageState.selected?.dragging
                                        ?.type === "pos" &&
                                        pageState.selected?.item?.id === job.id &&
                                        pageState.selected.dragging.dragStarted === true}
                                    isDraggingDurRightStart={pageState.selected
                                        ?.dragging?.type === "rightDur" &&
                                        pageState.selected?.item?.id === job.id}
                                    isDraggingDurLeftStart={pageState.selected
                                        ?.dragging?.type === "leftDur" &&
                                        pageState.selected?.item?.id === job.id}
                                    canDragLeft={isFirst}
                                    canDragRight={isLast}
                                    posDragLeftOffsetPx={isDragging ? 0 : segmentLeftPx - jobLeftPx}
                                    fullJobWidthPx={isDragging ? undefined : jobWidthPx}
                                    onDoubleClick={onJobDoubleClick}
                                    {view}
                                    {pageState}
                                    {validationStore}
                                    {relatedJobsMap}
                                />
                            </div>
                        {/each}
                    {/if}
                {/each}

                <!-- Site Visits Rendering -->
                {#if siteVisitsStore}
                    {@const filteredVehicleIds = new Set(filteredVehicles.map(v => v.id))}
                    {#each Array.from(siteVisitsStore.values()) as siteVisit(siteVisit.id)}
                        {#if siteVisit.start !== null && siteVisit.vehicle !== null && siteVisit.duration !== null && filteredVehicleIds.has(siteVisit.vehicle.id) && isJobInDisplayRange({
                            zoom: view.currentZoomStore,
                            jobStart: siteVisit.start,
                            jobDurationSec: siteVisit.duration
                        })}
                            {@const pos =
                                pageState.selected?.item?.id === siteVisit.id &&
                                pageState.selected.dragging
                                    ? {
                                        ...pageState.selected.dragging.hoveringPos,
                                        width: Math.max(30, pageState.selected.dragging.hoveringPos.width),
                                    }
                                    : {
                                        left: 0,
                                        top: 0,
                                        width: Math.max(30, getDurationSecInPx(
                                            view.currentZoomStore,
                                            siteVisit.duration,
                                        )),
                                    }}
                            <div
                                class="job-wrapper site-visit-wrapper"
                                data-start={siteVisit.start.toLocaleString()}
                                data-cell-step={view.currentZoomStore.cellPx}
                                style:left={`${getJobLeftOffset(siteVisit)}px`}
                                style:top={`${getJobTopOffset(siteVisit)}px`}
                                style="position: absolute; z-index: 10;"
                            >
                                <DragJob
                                    job={siteVisit}
                                    onDragPosStart={(e, rect) =>
                                        onMouseDownPos(e, rect, filteredVehicles, filteredOverlappingJobs)}
                                    onDragDurRightStart={(e, startDragDur, rect) =>
                                        onMouseDownRightDur(e, startDragDur, rect, filteredVehicles, filteredOverlappingJobs)}
                                    onDragDurLeftStart={(e, leftRect) =>
                                        onMouseDownLeftDur(e, leftRect, filteredVehicles, filteredOverlappingJobs)}
                                    left={pos.left}
                                    top={pos.top}
                                    widthPx={pos.width}
                                    heightPx={rowStepPx}
                                    isDraggingPos={pageState.selected?.dragging
                                        ?.type === "pos" &&
                                        pageState.selected?.item?.id === siteVisit.id}
                                    isDraggingDurRightStart={pageState.selected
                                        ?.dragging?.type === "rightDur" &&
                                        pageState.selected?.item?.id === siteVisit.id}
                                    isDraggingDurLeftStart={pageState.selected
                                        ?.dragging?.type === "leftDur" &&
                                        pageState.selected?.item?.id === siteVisit.id}
                                    canDragLeft={true}
                                    canDragRight={true}
                                    onDoubleClick={onJobDoubleClick}
                                    {view}
                                    {pageState}
                                    {validationStore}
                                    {relatedJobsMap}
                                    isSiteVisit={true}
                                />
                            </div>
                        {/if}
                    {/each}
                {/if}
            </div>
            <div class="drop-target-wrapper">
                {#if isStateJob(pageState) && pageState.selected?.dragging?.dropPreview?.start}
                    {@const dropPreview = pageState.selected.dragging.dropPreview}
                    {@const dropTopOffset = dropPreview.vehicle
                        ? getJobTopOffsetForVehicle(dropPreview.vehicle, filteredVehicles, filteredOverlappingJobs)
                        : getJobTopOffset(pageState.selected.item)}
                    {#each splitOnWeekends(dropPreview.start, dropPreview.duration, pageState.selected.item.weekend_overrides) as segment, segIdx (segIdx)}
                        <div
                            class="drop-target"
                            style:--left={`${getPxFromStart(view.currentZoomStore, segment.start)}px`}
                            style:--top={`${dropTopOffset}px`}
                            style:--width={`${getDurationSecInPx(view.currentZoomStore, segment.durationSec)}px`}
                            style:--height={`${rowStepPx}px`}
                        ></div>
                    {/each}
                {/if}
            </div>
        </div>
    </div>
    <div class="scheduler-col-lines" onwheel={onWheelZoom}>
        <SchedulerColLines
            currentZoom={view.currentZoomStore}
            {rowLineHeight}
            {pageState}
        />
    </div>
</div>

<!--<Debug {sites} {filteredSites} />-->

<style lang="scss">
    .wrapper {
        height: 95%;
        width: 100%;
        position: relative;
        display: grid;
        grid-template-rows: max-content 1fr;
        grid-template-columns: var(--users-width) 1fr;
        grid-template-areas:
            "users-title scheduler-header"
            "users content";
        overflow: auto;
    }

    .users-header {
        grid-area: users-title;
        position: sticky;
        left: 0;
        top: 0;
    }

    .users {
        grid-area: users;
        position: sticky;
        left: 0;
        z-index: 20;
        background: var(--background);
        box-shadow: 0 0 0.5rem 0 hsl(0 0% 0% / 0.4);
    }

    .scheduler-header {
        grid-area: scheduler-header;
        position: sticky;
        top: 0;
        z-index: 20;
    }

    .now-line-wrapper {
        z-index: 20;
        pointer-events: none;
        grid-area: 1 / 2 / 3 / 4;
        position: sticky;
        top: 0px;
    }

    .content {
        grid-area: content;
        height: 100%;
        display: grid;
        grid-template-rows: max-content;
        cursor: grab;
    }

    .scheduler-col-lines {
        grid-area: content;
    }

    .rows-wrapper {
        flex: 1;
        position: relative;
    }

    .drop-target {
        position: absolute;
        background: var(--primary);
        width: var(--width);
        height: var(--height);
        left: var(--left);
        top: var(--top);
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: grab;
        border-radius: 5px;
        opacity: 0.5;
    }

    :global(.calendars-container) {
        z-index: 100;
    }

    .job-wrapper {
        transition: top 150ms;
    }
</style>
